import { Col, Edit, Divider, Form, Input, Row, SaveButton, Select, Typography, useForm, useSelect } from "@pankod/refine-antd";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { useCan, useOne } from "@pankod/refine-core";
import { useEffect, useMemo, useState } from "react";
import { ISawAsset, ISawAssetMetadata, ILookup, ILocations } from "interfaces";
import { DATAPROVIDER_UPDATE, DATAPROVIDER_LOOKUP, RESOURCE_PATH, STALE_DURATION, DATAPROVIDER_CONFIGSET, DATAPROVIDER_READ, generateGroupFetchID } from "scripts/site";
import { EditSawAssetConfig } from "../asset2/editAsset2Config";
import { ShowUserCard } from "components/profile/showUserCard";
import { arrayExtensions } from "lib-common";
import type { Rule } from "antd/es/form";

type SawAssetConfigOverrides = Partial<ISawAssetMetadata["config"]> & {
  editableStates?: string[];
  associateDetailsRequired?: boolean;
};


export const SawAssetEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm<ISawAsset>({
    dataProviderName: DATAPROVIDER_UPDATE,
  });
  const originalOnFinish = formProps.onFinish;

  const { data: metadata } = useOne<ISawAssetMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.SAW_ASSET,
    queryOptions: {
      enabled: true,
      staleTime: STALE_DURATION
    }
  });
  const metaConfig = metadata?.data.config;
  // const metaFields = metadata?.data.fields; // dead code path retained for future metadata-driven UI
  let record = queryResult?.data?.data as ISawAsset | undefined;
  const { data: configSet } = useOne<{ values?: SawAssetConfigOverrides }>(
    {
      dataProviderName: DATAPROVIDER_CONFIGSET,
      resource: RESOURCE_PATH.SAW_ASSET,
      id: record?.id ?? "",
      queryOptions: {
        enabled: !!record?.id,
      }
    }
  );
  const configSetValues = configSet?.data?.values;
  const effectiveConfig = useMemo(
    () => ({
      ...(metaConfig ?? {}),
      ...(configSetValues ?? {}),
    }) as SawAssetConfigOverrides,
    [metaConfig, configSetValues]
  );
  const effectiveEditableStates = useMemo(() => {
    const configuredStates = effectiveConfig?.editableStates;
    if (!Array.isArray(configuredStates)) {
      return [] as string[];
    }
    return configuredStates
      .map((state) => (typeof state === "string" ? state.trim() : ""))
      .filter((state): state is string => state.length > 0);
  }, [effectiveConfig?.editableStates]);

  const assignedUsers = useMemo(() => {
    const assignedList = record?.stateManager?.assignedTo;
    if (Array.isArray(assignedList)) {
      return assignedList
        .map((value) => (typeof value === "string" ? value.trim() : String(value)))
        .filter((value): value is string => value.length > 0);
    }
    if (record?.assignedTo) {
      const singleValue = typeof record.assignedTo === "string"
        ? record.assignedTo.trim()
        : String(record.assignedTo);
      return singleValue.length > 0 ? [singleValue] : [];
    }
    return [] as string[];
  }, [record?.assignedTo, record?.stateManager?.assignedTo]);

  const normalizedCurrentState = useMemo(() => {
    if (!record?.stateManager?.state) {
      return undefined;
    }
    return record.stateManager.state.trim().toLowerCase();
  }, [record?.stateManager?.state]);
  const canEditPermission = useCan({
    resource: RESOURCE_PATH.SAW_ASSET,
    action: "edit",
    params: {
      dataProviderName: DATAPROVIDER_UPDATE,
      id: record?.id,
    },
    queryOptions: {
      enabled: !!record?.id,
    }
  });
  const allowEditing = useMemo(() => {
    if (!canEditPermission.data?.can) {
      return false;
    }
    if (!effectiveEditableStates.length) {
      return true;
    }
    if (!normalizedCurrentState) {
      return true;
    }
    const normalizedEditableStates = effectiveEditableStates.map((state) => state.trim().toLowerCase());
    return normalizedEditableStates.includes(normalizedCurrentState);
  }, [canEditPermission.data?.can, effectiveEditableStates, normalizedCurrentState]);
  const displayAssetId = record?.sawAssetId ?? record?.id ?? "---";
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(record?.countryId);

  useEffect(() => {
    const resolvedId = record?.id;
    if (!formProps.form || !resolvedId) {
      return;
    }

    formProps.form.setFieldsValue({
      id: resolvedId,
      sawAssetId: record?.sawAssetId ?? resolvedId,
      deliveryPartner: Array.isArray(record?.deliveryPartner)
        ? record?.deliveryPartner[0]
        : record?.deliveryPartner,
      associateLocation: record?.associateLocation,
      deliveryHead: record?.deliveryHead,
      stateAction: record?.stateManager?.state,
    });
  }, [
    formProps.form,
    record?.id,
    record?.sawAssetId,
    record?.stateManager?.state,
    record?.deliveryPartner,
    record?.associateLocation,
    record?.deliveryHead,
  ]);

  useEffect(() => {
    if (record?.countryId) {
      setSelectedCountry(record.countryId);
    }
  }, [record?.countryId]);

  const accessoriesOptions = (effectiveConfig?.accessories ?? []).filter((item): item is string => !!item);
  const makeModelOptions = (effectiveConfig?.makeModel ?? []).filter((item): item is string => !!item);
  const resolutionOptions = (effectiveConfig?.resolution ?? []).filter((item): item is string => !!item);

  const { selectProps: countryProps } = useSelect<ILookup>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.COUNTRY,
    optionLabel: "name",
    optionValue: "id",
    sort: [{ field: "name", order: "asc" }],
    fetchSize: 100,
    filters: [
      { field: "sawAsset.enabled", operator: "eq", value: true }
    ]
  });

  const { queryResult: locationPropsAll } = useSelect<ILocations>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.LOCATION,
    optionLabel: "name",
    optionValue: "id",
    sort: [{ field: "name", order: "asc" }],
    fetchSize: 100
  });
  const locations = locationPropsAll.data?.data;

  const primaryAssignedUserId = assignedUsers[0];
  const associateDetailsRequired = effectiveConfig?.associateDetailsRequired !== false;
  const recordHasAssociateDetails = useMemo(() => (
    Boolean(record?.tcsEmployeeId && String(record.tcsEmployeeId).trim().length > 0)
  ), [record?.tcsEmployeeId]);
  const showAssociateSection = associateDetailsRequired && recordHasAssociateDetails;

  useEffect(() => {
    if (!formProps.form) {
      return;
    }

    formProps.form.setFieldsValue({
      assignedTo: primaryAssignedUserId ?? undefined,
      tcsEmployeeId: record?.tcsEmployeeId ?? undefined,
      projectName: record?.projectName ?? undefined,
      associateLocation: record?.associateLocation ?? undefined,
      deliveryHead: record?.deliveryHead ?? undefined,
      deliveryPartner: Array.isArray(record?.deliveryPartner)
        ? record?.deliveryPartner[0]
        : record?.deliveryPartner,
    });
  }, [
    formProps.form,
    primaryAssignedUserId,
    record?.associateLocation,
    record?.deliveryHead,
    record?.deliveryPartner,
    record?.projectName,
    record?.tcsEmployeeId,
  ]);

  const associateLocationOptions = useMemo(() => {
    if (!locations || locations.length === 0) {
      return [] as { label: string; value: string }[];
    }
    const targetCountryId = selectedCountry ?? record?.countryId;
    return locations
      .filter((loc) => !targetCountryId || loc.countryId === targetCountryId)
      .map((loc) => ({ label: loc.name, value: loc.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [locations, selectedCountry, record?.countryId]);

  const { selectProps: msInventoryLocationProps } = useSelect<ILocations>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.LOCATION,
    optionLabel: "name",
    optionValue: "id",
    filters: [
      { field: "countryId", operator: "eq", value: selectedCountry },
      { field: "sawasset.enabled", operator: "eq", value: true }
    ],
    sort: [{ field: "name", order: "asc" }],
    fetchSize: 100,
    queryOptions: { enabled: !!selectedCountry }
  });

  const effectiveMsInventoryLocationOptions = (msInventoryLocationProps.options && msInventoryLocationProps.options.length > 0)
    ? msInventoryLocationProps.options : [];

  const { selectProps: tcsBranchProps } = useSelect<ILookup>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.LOCATION,
    optionLabel: "name",
    optionValue: "id",
    sort: [{ field: "name", order: "asc" }],
    fetchSize: 100
  });

  const { data: resourcingMetadata } = useOne<{
    config?: {
      deliveryHeadGroup?: string;
      deliveryPartnerOnsiteGroup?: string;
      deliveryPartnerOffshoreGroup?: string;
    };
  }>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.RESOURCINGREQUEST,
    queryOptions: {
      enabled: true,
      staleTime: STALE_DURATION,
    }
  });

  const deliveryHeadGroupId = resourcingMetadata?.data?.config?.deliveryHeadGroup;
  const deliveryPartnerOnsiteGroupId = resourcingMetadata?.data?.config?.deliveryPartnerOnsiteGroup;
  const deliveryPartnerOffshoreGroupId = resourcingMetadata?.data?.config?.deliveryPartnerOffshoreGroup;

  const { data: deliveryHeadGroup, isLoading: deliveryHeadLoading } = useOne<ILookup[]>({
    dataProviderName: DATAPROVIDER_READ,
    resource: RESOURCE_PATH.GROUPS,
    id: generateGroupFetchID(deliveryHeadGroupId),
    queryOptions: {
      enabled: !!deliveryHeadGroupId,
      staleTime: STALE_DURATION,
    }
  });

  const deliveryHeadOptions = useMemo(() => (
    arrayExtensions
      .sortByName(deliveryHeadGroup?.data || [])
      .map((item) => ({ label: item.name, value: item.id }))
  ), [deliveryHeadGroup?.data]);

  const { data: deliveryPartnerOnsiteGroup, isLoading: deliveryPartnerOnsiteLoading } = useOne<ILookup[]>({
    dataProviderName: DATAPROVIDER_READ,
    resource: RESOURCE_PATH.GROUPS,
    id: generateGroupFetchID(deliveryPartnerOnsiteGroupId),
    queryOptions: {
      enabled: !!deliveryPartnerOnsiteGroupId,
      staleTime: STALE_DURATION,
    }
  });

  const { data: deliveryPartnerOffshoreGroup, isLoading: deliveryPartnerOffshoreLoading } = useOne<ILookup[]>({
    dataProviderName: DATAPROVIDER_READ,
    resource: RESOURCE_PATH.GROUPS,
    id: generateGroupFetchID(deliveryPartnerOffshoreGroupId),
    queryOptions: {
      enabled: !!deliveryPartnerOffshoreGroupId,
      staleTime: STALE_DURATION,
    }
  });

  const deliveryPartnerOptions = useMemo(() => {
    const onsite = arrayExtensions
      .sortByName(deliveryPartnerOnsiteGroup?.data || [])
      .map((item) => ({ label: item.name, value: item.id }));
    const offshore = arrayExtensions
      .sortByName(deliveryPartnerOffshoreGroup?.data || [])
      .map((item) => ({ label: item.name, value: item.id }));
    if (!onsite.length && !offshore.length) {
      return [] as { label: string; value: string }[];
    }
    const seen = new Set<string>();
    const combined = [...onsite, ...offshore];
    return combined.filter((option) => {
      if (seen.has(option.value)) {
        return false;
      }
      seen.add(option.value);
      return true;
    });
  }, [deliveryPartnerOffshoreGroup?.data, deliveryPartnerOnsiteGroup?.data]);

  const deliveryPartnerLoading = deliveryPartnerOnsiteLoading || deliveryPartnerOffshoreLoading;

  const filterOptionByLabel = (input: string, option?: { label?: unknown }) => {
    if (!option?.label) {
      return false;
    }
    const labelText = String(option.label).toLowerCase();
    return labelText.includes(input.toLowerCase());
  };

  const twoColumnLayout = {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 12 },
    lg: { span: 12 },
    xl: { span: 12 },
    xxl: { span: 12 },
  };

  const rowGutter = { xs: 8, sm: 16, md: 24, lg: 32, xl: 40, xxl: 48 };

  const handleFinish = (values: Record<string, unknown>) => {
    if (!allowEditing) {
      return;
    }

    const typedValues = values as unknown as ISawAsset;
    const normalizeString = (value: unknown): string | undefined => {
      if (value === undefined || value === null) {
        return undefined;
      }
      if (Array.isArray(value)) {
        return undefined;
      }
      const normalized = String(value).trim();
      return normalized.length > 0 ? normalized : undefined;
    };

    const normalizedDeliveryPartner = Array.isArray(typedValues.deliveryPartner)
      ? typedValues.deliveryPartner
          .map((entry) => normalizeString(entry))
          .filter((entry): entry is string => entry !== undefined)
      : (() => {
          const single = normalizeString(typedValues.deliveryPartner);
          return single ? [single] : [];
        })();

    const normalizedAssignedTo = normalizeString(typedValues.assignedTo);
    const normalizedTcsEmployeeId = normalizeString(typedValues.tcsEmployeeId);
    const normalizedProjectName = normalizeString(typedValues.projectName);
    const normalizedAssociateLocation = normalizeString(typedValues.associateLocation);
    const normalizedDeliveryHead = normalizeString(typedValues.deliveryHead);
    const coerceNumber = (value: unknown) => {
      if (value === undefined || value === null || value === "") {
        return undefined;
      }
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };
    const poDetailsValue = coerceNumber(typedValues.poDetails);
    const deviceValueNumber = coerceNumber(typedValues.deviceValue);

    const payload: Partial<ISawAsset> = {
      ...typedValues,
      stateAction: typedValues.stateAction,
      dateOfDelivery: typedValues.dateOfDelivery
        ? dayjs(typedValues.dateOfDelivery).toISOString()
        : undefined,
    };

    const payloadWithNameEmail = payload as ISawAsset & { nameEmail?: string };
    const existingNameEmail = (record as { nameEmail?: string } | undefined)?.nameEmail;
    if (typeof (typedValues as { nameEmail?: unknown })?.nameEmail === "string") {
      payloadWithNameEmail.nameEmail = (typedValues as { nameEmail?: string }).nameEmail;
    } else if (existingNameEmail !== undefined) {
      payloadWithNameEmail.nameEmail = existingNameEmail;
    }

    if (poDetailsValue !== undefined) {
      (payload as Partial<ISawAsset>).poDetails = poDetailsValue as ISawAsset["poDetails"];
    }
    if (deviceValueNumber !== undefined) {
      (payload as Partial<ISawAsset>).deviceValue = deviceValueNumber as ISawAsset["deviceValue"];
    }
    const typedHasAssociateDetails = Boolean(normalizedTcsEmployeeId);
    const shouldPersistAssociateDetails = associateDetailsRequired && typedHasAssociateDetails;
    if (shouldPersistAssociateDetails) {
      if (normalizedDeliveryPartner.length > 0) {
        payload.deliveryPartner = normalizedDeliveryPartner as ISawAsset["deliveryPartner"];
      } else {
        delete payload.deliveryPartner;
      }

      if (normalizedAssignedTo) {
        payload.assignedTo = normalizedAssignedTo as ISawAsset["assignedTo"];
      } else if (typeof record?.assignedTo === "string" && record.assignedTo.trim().length > 0) {
        payload.assignedTo = record.assignedTo.trim() as ISawAsset["assignedTo"];
      }

      if (normalizedTcsEmployeeId) {
        payload.tcsEmployeeId = normalizedTcsEmployeeId as ISawAsset["tcsEmployeeId"];
      } else if (typeof record?.tcsEmployeeId === "string" && record.tcsEmployeeId.trim().length > 0) {
        payload.tcsEmployeeId = record.tcsEmployeeId.trim() as ISawAsset["tcsEmployeeId"];
      }

      if (normalizedProjectName !== undefined) {
        payload.projectName = normalizedProjectName as ISawAsset["projectName"];
      } else {
        delete payload.projectName;
      }

      if (normalizedAssociateLocation) {
        payload.associateLocation = normalizedAssociateLocation as ISawAsset["associateLocation"];
      } else {
        delete payload.associateLocation;
      }

      if (normalizedDeliveryHead) {
        payload.deliveryHead = normalizedDeliveryHead as ISawAsset["deliveryHead"];
      } else {
        delete payload.deliveryHead;
      }
    } else {
      delete payload.assignedTo;
      delete payload.projectName;
      delete payload.associateLocation;
      delete payload.deliveryHead;
      delete payload.deliveryPartner;
      delete (payload as { tcsEmployeeId?: unknown }).tcsEmployeeId;
    }

    void originalOnFinish?.(payload as ISawAsset);
  };

  const optionalPoDetailsRule: Rule = {
    validator: (_, value) => {
      if (value === undefined || value === null) {
        return Promise.resolve();
      }
      const normalized = String(value).trim();
      if (!normalized) {
        return Promise.resolve();
      }
      if (normalized.length < 5 || normalized.length > 20) {
        return Promise.reject(new Error("PO Details must be between 5 and 20 digits"));
      }
      if (!/^[0-9]+$/.test(normalized)) {
        return Promise.reject(new Error("PO Details must be numeric only"));
      }
      return Promise.resolve();
    },
  };

  const optionalNumericValueRule: Rule = {
    validator: (_, value) => {
      if (value === undefined || value === null) {
        return Promise.resolve();
      }
      const normalized = String(value).trim();
      if (!normalized) {
        return Promise.resolve();
      }
      if (!/^[0-9]+$/.test(normalized)) {
        return Promise.reject(new Error("Value must be numbers only"));
      }
      return Promise.resolve();
    },
  };

  return (
    <Edit
      title="Edit SAW Asset"
      footerButtons={() => (
        allowEditing ? <SaveButton {...saveButtonProps} /> : null
      )}
    >
      <Form {...formProps} layout="vertical" autoComplete="off" onFinish={handleFinish}>
        {showAssociateSection && (
          <>
            {/* Associate Details Section */}
            <Divider orientation="left">Associate Details</Divider>
            <Row gutter={rowGutter}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Typography.Title level={5}>Emp Name (Allocated to)</Typography.Title>
                {assignedUsers.length > 0
                  ? assignedUsers.map((id, index) => <ShowUserCard key={id || index} id={id} />)
                  : <Typography.Text>---</Typography.Text>}
              </Col>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Typography.Title level={5}>TCS Employee Id</Typography.Title>
                <Typography.Text>{record?.tcsEmployeeId ?? "---"}</Typography.Text>
              </Col>
            </Row>
            <Row gutter={rowGutter} style={{ marginTop: 12 }}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Form.Item
                  label="Project Name"
                  name="projectName"
                  rules={[
                    { required: true, message: "Please enter Project Name" },
                    { type: "string", whitespace: true },
                  ]}
                >
                  <Input placeholder="Enter Project Name" disabled={!allowEditing} />
                </Form.Item>
              </Col>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Form.Item
                  label="Associate Location"
                  name="associateLocation"
                  rules={[{ required: true, message: "Please select Associate Location" }]}
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select Associate Location"
                    optionFilterProp="label"
                    filterOption={(input, option) => {
                      if (!option?.label) {
                        return false;
                      }
                      const labelValue = String(option.label).toLowerCase();
                      return labelValue.includes(input.toLowerCase());
                    }}
                    options={associateLocationOptions}
                    disabled={!allowEditing}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={rowGutter} style={{ marginTop: 12 }}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Form.Item
                  label="Delivery Head (L1)"
                  name="deliveryHead"
                  rules={[{ required: true, message: "Please select Delivery Head" }]}
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select Delivery Head"
                    optionFilterProp="label"
                    filterOption={filterOptionByLabel}
                    options={deliveryHeadOptions}
                    loading={deliveryHeadLoading}
                    disabled={!allowEditing}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Form.Item
                  label="Delivery Partner (L2)"
                  name="deliveryPartner"
                  rules={[{ required: true, message: "Please select Delivery Partner" }]}
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select Delivery Partner"
                    optionFilterProp="label"
                    filterOption={filterOptionByLabel}
                    options={deliveryPartnerOptions}
                    loading={deliveryPartnerLoading}
                    disabled={!allowEditing}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* Asset Information Section */}
        <Divider orientation="left">Asset Information</Divider>
        <Row gutter={rowGutter}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Typography.Title level={5} style={{ marginBottom: '8px' }}>SAW Asset ID</Typography.Title>
            <Typography.Text>{displayAssetId}</Typography.Text>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              tooltip={<> 
                Launch windows Powershell and execute below command to identify the Computer Serial Number:
                <br />
                <strong>Gwmi win32_Bios | Select -Expand SerialNumber</strong>
              </>}
              label="Serial Number"
              name="serialNumber"
              rules={[
                {
                  required: true,
                  type: "string",
                  whitespace: true,
                  min: 5,
                  max: 20,
                }
              ]}
            >
              <Input disabled={!allowEditing} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={rowGutter}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="PO Details"
              name="poDetails"
              rules={[optionalPoDetailsRule]}
            >
              <Input placeholder="Enter PO Number" disabled={!allowEditing} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Date of Delivery"
              name="dateOfDelivery"
              tooltip="Asset delivery date (cannot be in the future or more than 2 years ago)"
              rules={[{ type: "date", message: "Please select Date of Delivery" }]}
              valuePropName="value"
              getValueProps={value => ({ value: value ? dayjs(value) : null })}
            >
              <DatePicker
                picker="date"
                style={{ width: "100%" }}
                allowClear
                placeholder="Select Date"
                format="DD/MM/YYYY"
                disabled={!allowEditing}
                disabledDate={selDate => selDate && (selDate > dayjs() || selDate < dayjs().subtract(24, 'months'))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Value of the Device"
              name="deviceValue"
              rules={[optionalNumericValueRule]}
            >
              <Input placeholder="Enter Value (numbers only)" disabled={!allowEditing} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Dock Serial Number"
              name="dockSerialNumber"
              rules={[
                {
                  required: false,
                  type: "string",
                  whitespace: true,
                  min: 5,
                  max: 20,
                },
                {
                  pattern: /^[a-zA-Z0-9]+$/,
                  message: "Dock Serial Number must be alphanumeric only"
                }
              ]}
            >
              <Input placeholder="Enter dock serial number (optional)" disabled={!allowEditing} />
            </Form.Item>
          </Col>
        </Row>

        {accessoriesOptions.length > 0 && (
          <Row gutter={rowGutter}>
            <Col className="gutter-row" {...twoColumnLayout}>
              <Form.Item
                label="Accessories"
                name="accessories"
                tooltip="Select all accessories provided with the asset."
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select Accessories"
                  options={accessoriesOptions.map((acc: string) => ({ label: acc, value: acc }))}
                  disabled={!allowEditing}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" {...twoColumnLayout}>
              {(makeModelOptions.length > 0 || resolutionOptions.length > 0) && (
                <EditSawAssetConfig
                  makeModel={makeModelOptions}
                  resolution={resolutionOptions}
                  // fields={metaFields} // dead code: uncomment when metadata fields power this section
                  disabled={!allowEditing}
                />
              )}
            </Col>
          </Row>
        )}

        {accessoriesOptions.length === 0 && (makeModelOptions.length > 0 || resolutionOptions.length > 0) && (
          <EditSawAssetConfig
            makeModel={makeModelOptions}
            resolution={resolutionOptions}
            // fields={metaFields} // dead code: uncomment when metadata fields power this section
            disabled={!allowEditing}
          />
        )}

        {/* Location Section */}
        <Divider orientation="left">Location</Divider>
        <Row gutter={rowGutter}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Country"
              name="countryId"
              rules={[
                {
                  required: true,
                  whitespace: true,
                },
              ]}
            >
              <Select
                allowClear
                placeholder="Select Country"
                showSearch
                options={countryProps.options}
                loading={countryProps.loading}
                onChange={(value: string) => {
                  setSelectedCountry(value);
                  formProps.form?.setFieldsValue({
                    tcsBranch: undefined,
                    msInventoryLocation: undefined,
                    associateLocation: undefined,
                  });
                }}
                disabled={!allowEditing}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="MS Inventory Location"
              name="msInventoryLocation"
              rules={[{ required: true, type: "string" }]}
            >
              <Select
                allowClear
                placeholder={selectedCountry ? "Select MS Inventory Location" : "Select Country first"}
                showSearch
                {...msInventoryLocationProps}
                options={effectiveMsInventoryLocationOptions}
                disabled={!allowEditing || !selectedCountry}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={rowGutter}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="TCS Branch"
              name="tcsBranch"
              rules={[
                {
                  required: true,
                  type: "string",
                }
              ]}
            >
              <Select
                allowClear
                placeholder="Select TCS Branch"
                showSearch
                {...tcsBranchProps}
                options={
                  selectedCountry
                    ? locations
                        ?.filter((loc: ILocations) => loc.countryId === selectedCountry)
                        ?.sort((a, b) => a.name.localeCompare(b.name))
                        ?.map((loc: ILocations) => ({ label: loc.name, value: loc.id }))
                    : tcsBranchProps.options
                }
                disabled={!allowEditing}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* Hidden Fields */}
        <Form.Item name="assignedTo" hidden initialValue={primaryAssignedUserId ?? undefined}>
          <Input />
        </Form.Item>

        <Form.Item name="tcsEmployeeId" hidden initialValue={record?.tcsEmployeeId}>
          <Input />
        </Form.Item>

        {!showAssociateSection && (
          <>
            <Form.Item name="projectName" hidden initialValue={record?.projectName}>
              <Input />
            </Form.Item>

            <Form.Item name="associateLocation" hidden initialValue={record?.associateLocation}>
              <Input />
            </Form.Item>

            <Form.Item name="deliveryHead" hidden initialValue={record?.deliveryHead}>
              <Input />
            </Form.Item>

            <Form.Item
              name="deliveryPartner"
              hidden
              initialValue={Array.isArray(record?.deliveryPartner) ? record?.deliveryPartner[0] : record?.deliveryPartner}
            >
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item name="id" hidden initialValue={record?.id}>
          <Input />
        </Form.Item>

        <Form.Item name="sawAssetId" hidden initialValue={record?.sawAssetId ?? record?.id}>
          <Input />
        </Form.Item>

        <Form.Item
          name="stateAction"
          hidden={true}
          initialValue={record?.stateManager?.state}
        >
          <Input />
        </Form.Item>

        <Form.Item name="name" hidden initialValue={record?.name ?? "SAW Asset"}>
          <Input />
        </Form.Item>
      </Form>
    </Edit>
  );
};

export default SawAssetEdit;
